/**
 * HTTP-backed {@link IapApi} for playground mode.
 *
 * - Wallet balance and spend call the playground-target Cloud Run directly.
 * - Subscription methods drive RevenueCat Web Billing via `@revenuecat/purchases-js`.
 *   Webhooks land on the same Cloud Run instance (`POST /v1/wallet/purchase-webhook`)
 *   and credit the user; `environment: SANDBOX` differentiates sandbox events server-side.
 *
 * ⚠️ PLAYGROUND-ONLY: depends on Firebase (`getFirebaseClient()`) and dynamically
 * imports `@revenuecat/purchases-js`. Don't re-export from `iap/index.ts` —
 * import directly from PlaygroundHost so these deps don't leak into the prod
 * RPC bundle.
 */
import { MockIapApi } from './MockIapApi'
import {
  SpendCurrencyOptions,
  SpendCurrencyResult,
  SubscriptionTier,
  SubscriptionInterval,
  RunSubscription,
  RunSubscriptionsResponse,
  PurchaseSubscriptionResponse,
  OpenStoreResult,
  assertTierSupportsInterval,
  DirectPurchaseSku,
} from './IapApi'
import { getFirebaseClient } from '../firebase/firebaseClient'
import { getCloudRunUrl, getPlaygroundConfig } from '../config/playground'
import { getPlaygroundGameSessionToken } from '../http/playgroundGameSession'
import { RundotApiError } from '../errors'

// Narrow structural shapes for the subset of `@revenuecat/purchases-js` we use.
// Avoids a hard dep on the package's types (it's an optional peer dep) and
// keeps `tsc --noEmit` clean for consumers that don't install RC.
type RcPurchasesInstance = {
  getOfferings(): Promise<RcOfferings>
  getCustomerInfo(): Promise<RcCustomerInfo>
  purchase(args: { rcPackage: RcPackage }): Promise<RcPurchaseResult>
  changeUser(appUserId: string): Promise<unknown>
}
type RcPurchaseResult = {
  storeTransaction: {
    transactionIdentifier: string
  }
}
type RcPurchasesCtor = {
  configure(apiKey: string, appUserId: string): RcPurchasesInstance
  getSharedInstance(): RcPurchasesInstance
}
export type RcOfferings = {
  current: RcOffering | null
  all: Record<string, RcOffering>
}
export type RcOffering = {
  identifier: string
  availablePackages: RcPackage[]
}
export type RcPackage = {
  identifier: string
  packageType: string
  webBillingProduct?: RcWebBillingProduct
  rcBillingProduct?: RcWebBillingProduct
}
export type RcWebBillingProduct = {
  identifier: string
  title?: string
  description?: string
  currentPrice?: { amountMicros?: number; currency?: string; formattedPrice?: string }
}
type RcCustomerInfo = {
  activeSubscriptions: Set<string>
  allPurchaseDatesByProduct: Record<string, Date | string | null>
  managementURL: string | null
}

// Mirrors the server-side `SUBSCRIPTION_TIERS` in
// `server/cloud-run/src/services/wallet.service.ts`. Lowest → highest.
// Subscription detection is substring-based on product identifiers, matching
// the server logic so a playground check returns the same answer the webhook
// processor would record.
const TIER_HIERARCHY: SubscriptionTier[] = ['LITE', 'CORE', 'PLUS', 'PRIME', 'ULTIMATE']

// Each subscription tier is published as its own RC offering (platform /
// RC-dashboard-owned). MUST stay identical to `tierToOfferingId` in
// client/services/revenuecat.ts — that native-host map is authoritative.
// The RC `current` offering is the RUN Bits pack offering (`run_game_iap`),
// NOT a subscription offering, so subscription lookups read these explicitly.
const TIER_TO_OFFERING_ID: Record<SubscriptionTier, string> = {
  LITE: 'run_game_lite_subscriptions',
  CORE: 'run_game_core_subscriptions',
  PLUS: 'run_game_plus_subscriptions',
  PRIME: 'run_game_prime_subscriptions',
  ULTIMATE: 'run_game_ultimate_subscriptions',
}

// RC's PackageType identifiers — the Web SDK uses the `$rc_*` package
// identifiers, matching `client/services/revenuecat.ts`. We only recognise
// the three intervals exposed in `SubscriptionInterval`; anything else
// (lifetime, custom, etc.) is ignored when mapping offerings → RunSubscriptions.
const PACKAGE_TYPE_TO_INTERVAL: Record<string, SubscriptionInterval> = {
  $rc_weekly: 'weekly',
  $rc_monthly: 'monthly',
  $rc_annual: 'annual',
}
const INTERVAL_TO_PACKAGE_TYPE: Record<SubscriptionInterval, string> = {
  weekly: '$rc_weekly',
  monthly: '$rc_monthly',
  annual: '$rc_annual',
}

async function callCloudRun<TResponse>(
  method: string,
  endpoint: string,
  body?: unknown,
): Promise<TResponse> {
  const client = await getFirebaseClient()
  const idToken = await client.getIdTokenOrThrow()
  const url = `${getCloudRunUrl()}${endpoint}`
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${idToken}`,
  }

  const options: RequestInit = {
    method,
    headers,
  }
  if (body !== undefined) {
    options.body = JSON.stringify(body)
  }

  const gst = await getPlaygroundGameSessionToken()
  if (gst) headers['x-h5-game-session'] = gst

  const response = await fetch(url, options)
  const text = await response.text()

  if (!response.ok) {
    let errorMessage = `Cloud Run ${method} ${endpoint} failed (${response.status})`
    try {
      const errorJson = JSON.parse(text)
      errorMessage = errorJson.error || errorJson.message || errorMessage
    } catch {
      if (text) errorMessage = text
    }
    throw new Error(errorMessage)
  }

  if (!text) return undefined as TResponse
  return JSON.parse(text) as TResponse
}

function formatPrice(product: RcWebBillingProduct | undefined): string {
  const price = product?.currentPrice
  if (!price) return ''
  if (price.formattedPrice) return price.formattedPrice
  if (typeof price.amountMicros === 'number') {
    const amount = (price.amountMicros / 1_000_000).toFixed(2)
    return `${price.currency ?? 'USD'} ${amount}`
  }
  return ''
}

function renderPackageRow(
  pkg: RcPackage,
  onPurchase: (pkg: RcPackage) => Promise<void>,
  onSuccess: () => void,
): HTMLElement {
  const product = pkg.webBillingProduct ?? pkg.rcBillingProduct
  const row = document.createElement('div')
  row.style.cssText = `
    padding: 12px 20px; border-bottom: 1px solid #f0f0f0;
    display: flex; align-items: center; gap: 12px;
  `

  const info = document.createElement('div')
  info.style.cssText = 'flex: 1; min-width: 0;'

  const name = document.createElement('div')
  name.textContent = product?.title ?? product?.identifier ?? pkg.identifier
  name.style.cssText = 'font-weight: 600; font-size: 15px; margin-bottom: 2px;'
  info.appendChild(name)

  if (product?.description) {
    const desc = document.createElement('div')
    desc.textContent = product.description
    desc.style.cssText = 'color: #666; font-size: 13px;'
    info.appendChild(desc)
  }

  const err = document.createElement('div')
  err.style.cssText = 'color: #c00; font-size: 12px; margin-top: 4px; display: none;'
  info.appendChild(err)

  const buyLabel = formatPrice(product) || 'Buy'
  const buyBtn = document.createElement('button')
  buyBtn.textContent = buyLabel
  buyBtn.style.cssText = `
    padding: 8px 16px; background: #2563eb; color: white;
    border: none; border-radius: 6px; font-weight: 600;
    cursor: pointer; white-space: nowrap;
  `
  buyBtn.addEventListener('click', async () => {
    buyBtn.disabled = true
    buyBtn.textContent = 'Processing…'
    buyBtn.style.opacity = '0.6'
    err.style.display = 'none'
    try {
      await onPurchase(pkg)
      onSuccess()
    } catch (e) {
      err.textContent = e instanceof Error ? e.message : 'Purchase failed'
      err.style.display = 'block'
      buyBtn.disabled = false
      buyBtn.textContent = buyLabel
      buyBtn.style.opacity = '1'
    }
  })

  row.appendChild(info)
  row.appendChild(buyBtn)
  return row
}

/**
 * Render an in-game store modal listing the given packages and resolve when
 * the user closes it. Each row triggers `onPurchase` on click; on success the
 * modal closes, on failure the error is shown inline and the row stays open.
 */
function showStoreModal(
  packages: RcPackage[],
  onPurchase: (pkg: RcPackage) => Promise<void>,
): Promise<void> {
  return new Promise((resolve) => {
    const overlay = document.createElement('div')
    overlay.style.cssText = `
      position: fixed; inset: 0; background: rgba(0,0,0,0.6);
      display: flex; align-items: center; justify-content: center;
      z-index: 10700; font-family: sans-serif;
    `

    const card = document.createElement('div')
    card.style.cssText = `
      background: white; color: black; border-radius: 12px;
      width: min(480px, 92vw); max-height: 80vh; overflow: hidden;
      display: flex; flex-direction: column; box-shadow: 0 10px 30px rgba(0,0,0,0.3);
    `

    const close = () => {
      overlay.remove()
      resolve()
    }

    const header = document.createElement('div')
    header.style.cssText = `
      padding: 16px 20px; border-bottom: 1px solid #eee;
      display: flex; align-items: center; justify-content: space-between;
    `
    const title = document.createElement('div')
    title.textContent = 'Store'
    title.style.cssText = 'font-weight: 700; font-size: 18px;'
    const closeBtn = document.createElement('button')
    closeBtn.textContent = '✕'
    closeBtn.style.cssText = `
      background: none; border: none; font-size: 20px;
      cursor: pointer; color: #666; padding: 0 4px;
    `
    closeBtn.addEventListener('click', close)
    header.appendChild(title)
    header.appendChild(closeBtn)

    const list = document.createElement('div')
    list.style.cssText = 'overflow-y: auto; padding: 8px 0;'

    if (packages.length === 0) {
      const empty = document.createElement('div')
      empty.textContent = 'No items available.'
      empty.style.cssText = 'padding: 24px; text-align: center; color: #666;'
      list.appendChild(empty)
    } else {
      for (const pkg of packages) {
        list.appendChild(renderPackageRow(pkg, onPurchase, close))
      }
    }

    card.appendChild(header)
    card.appendChild(list)
    overlay.appendChild(card)
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) close()
    })
    document.body.appendChild(overlay)
  })
}

// One Purchases.configure() per process per appUserId. The RC Web SDK is a
// singleton; re-configuring with a different uid throws. Mirrors the
// `ensureConfigured` helper in `admin/src/creatorBilling/sandbox.service.ts`.
let rcConfiguredUid: string | null = null
// Single-flight queue so concurrent callers (e.g. parallel getSubscriptions +
// isUserSubscribed at startup) don't both reach configure() and crash the
// second one. New calls chain behind the in-flight one.
let rcInitQueue: Promise<unknown> = Promise.resolve()

// RC Web signals a dismissed checkout as a PurchasesError whose `errorCode`
// equals `ErrorCode.UserCancelledError`. The enum value is read off the
// dynamically imported module in doEnsureRcConfigured (no static dep on the
// optional peer package); 1 is its documented stable value, used until then.
let rcUserCancelledCode: unknown = 1

// Exported for tests.
export function isRcUserCancelled(error: unknown): boolean {
  const code = (error as { errorCode?: unknown } | null)?.errorCode
  return code !== undefined && code === rcUserCancelledCode
}

const DIRECT_PURCHASE_RECEIPT_PREFIX = 'rundot:direct-purchase-receipt:'

function directPurchaseReceiptKey(orderId: string): string {
  return `${DIRECT_PURCHASE_RECEIPT_PREFIX}${orderId}`
}

function loadDirectPurchaseReceipt(orderId: string): {
  provider: 'revenuecat'
  transactionId: string
} | null {
  if (typeof localStorage === 'undefined') return null
  try {
    const raw = localStorage.getItem(directPurchaseReceiptKey(orderId))
    if (!raw) return null
    const parsed = JSON.parse(raw) as { provider?: unknown; transactionId?: unknown }
    return parsed.provider === 'revenuecat' && typeof parsed.transactionId === 'string'
      ? { provider: parsed.provider, transactionId: parsed.transactionId }
      : null
  } catch (error) {
    void error
    return null
  }
}

function saveDirectPurchaseReceipt(
  orderId: string,
  receipt: { provider: 'revenuecat'; transactionId: string },
): void {
  if (typeof localStorage === 'undefined') return
  try {
    localStorage.setItem(directPurchaseReceiptKey(orderId), JSON.stringify(receipt))
  } catch (error) {
    void error
  }
}

/**
 * Dynamic import routed through a function parameter so the module specifier
 * isn't visible as a static literal at the `import(...)` call site. Vite's
 * `import-analysis` plugin tries to pre-resolve any dynamic import whose
 * argument is a literal string — and would crash the dev server with
 * "Failed to resolve import" when RC isn't installed (which is the exact
 * scenario our overlay is designed to handle gracefully). The `@vite-ignore`
 * comment only works for the `dynamic-import-vars` plugin, not this one, so
 * we defeat the literal detection structurally instead.
 */
async function importByName(name: string): Promise<unknown> {
  return import(name)
}

/**
 * Did the Vite playground plugin flag `@revenuecat/purchases-js` as missing
 * at dev-server startup? The plugin reads the project's `package.json` and
 * stashes the list of missing deps on `window` — see `RundotGamePlaygroundPlugin`.
 *
 * We can't reliably distinguish "module isn't installed" from "module is
 * installed but the chunk failed to load" from the dynamic-import error
 * alone (both surface as `Failed to fetch dynamically imported module` in
 * Vite). The plugin's static check is the authoritative signal.
 */
function isRevenueCatFlaggedMissing(): boolean {
  if (typeof window === 'undefined') return false
  const info = (window as unknown as {
    __RUNDOT_GAME_PLAYGROUND_MISSING_DEPS__?: { deps?: Array<{ packageName?: string }> } | null
  }).__RUNDOT_GAME_PLAYGROUND_MISSING_DEPS__
  if (!info || !Array.isArray(info.deps)) return false
  return info.deps.some((d) => d?.packageName === '@revenuecat/purchases-js')
}

async function doEnsureRcConfigured(
  apiKey: string,
  appUserId: string,
): Promise<RcPurchasesInstance> {
  let mod: unknown
  try {
    mod = await importByName('@revenuecat/purchases-js')
  } catch (err) {
    // Only swap the error for the install hint when the plugin definitively
    // flagged RC as missing at startup. Any other dynamic-import failure
    // (corrupt install, version conflict, transient chunk fetch error)
    // propagates unchanged so the real cause is visible in the stack.
    if (!isRevenueCatFlaggedMissing()) throw err
    throw new Error(
      '[RUN:playground] @revenuecat/purchases-js is not installed.\n' +
      'IAP / subscription methods require it in playground mode.\n' +
      'Run: npm install --save-dev @revenuecat/purchases-js@^1.39.1',
    )
  }
  const Purchases = (mod as { Purchases: RcPurchasesCtor }).Purchases
  const errorCodes = (mod as { ErrorCode?: { UserCancelledError?: unknown } }).ErrorCode
  if (errorCodes?.UserCancelledError !== undefined) {
    rcUserCancelledCode = errorCodes.UserCancelledError
  }

  if (rcConfiguredUid === null) {
    Purchases.configure(apiKey, appUserId)
    rcConfiguredUid = appUserId
    return Purchases.getSharedInstance()
  }
  const instance = Purchases.getSharedInstance()
  if (rcConfiguredUid !== appUserId) {
    await instance.changeUser(appUserId)
    rcConfiguredUid = appUserId
  }
  return instance
}

async function ensureRcConfigured(apiKey: string, appUserId: string): Promise<RcPurchasesInstance> {
  const next = rcInitQueue.then(
    () => doEnsureRcConfigured(apiKey, appUserId),
    () => doEnsureRcConfigured(apiKey, appUserId),
  )
  // Swallow rejection on the queue itself so a single failure doesn't poison
  // every subsequent caller; each caller still sees its own rejection via `next`.
  rcInitQueue = next.catch(() => undefined)
  return next
}

/**
 * Substring-match a product identifier against the tier hierarchy and return
 * the highest matching tier index (or -1 if none match). Mirrors the server
 * logic in `WalletService.isPlayerSubscribed`.
 */
function highestTierIndexFor(productIds: Iterable<string>): number {
  const lowered = Array.from(productIds, (p) => p.toLowerCase())
  return TIER_HIERARCHY.reduce(
    (highest, tier, idx) =>
      lowered.some((id) => id.includes(tier.toLowerCase())) ? idx : highest,
    -1,
  )
}

function packageToRunSubscription(pkg: RcPackage): RunSubscription | null {
  const interval = PACKAGE_TYPE_TO_INTERVAL[pkg.packageType]
  if (!interval) return null
  const product = pkg.webBillingProduct ?? pkg.rcBillingProduct
  const currentPrice = product?.currentPrice
  const amountMicros = currentPrice?.amountMicros
  if (typeof amountMicros !== 'number') return null
  return {
    currencyCode: currentPrice?.currency ?? 'USD',
    interval,
    price: amountMicros / 1_000_000,
    description: product?.description ?? product?.title ?? product?.identifier ?? pkg.identifier,
  }
}

/**
 * Build a {@link RunSubscriptionsResponse} from RC offerings by reading each
 * tier's dedicated offering from `offerings.all`. When `tier` is given, only
 * that tier is included. Never reads `offerings.current` — that is the Bucks
 * pack offering, not a subscription offering. Mirrors the native host
 * (client/services/revenuecat.ts `getSubscriptions`).
 */
export function offeringsToSubscriptions(
  offerings: RcOfferings,
  tier?: SubscriptionTier,
): RunSubscriptionsResponse {
  const tiers = tier ? [tier] : TIER_HIERARCHY
  const result: RunSubscriptionsResponse = {}
  for (const t of tiers) {
    // Always emit the tier key (default []) so the playground response shape
    // matches the prod bridge contract (H5IapWalletHandler maps the native
    // host's output, which carries every requested tier key via `?? []`).
    const subscriptions: RunSubscription[] = []
    const offering = offerings.all[TIER_TO_OFFERING_ID[t]]
    if (offering) {
      for (const pkg of offering.availablePackages) {
        const mapped = packageToRunSubscription(pkg)
        if (mapped) subscriptions.push(mapped)
      }
    }
    result[t] = subscriptions
  }
  return result
}

/**
 * Find the RC package for a tier + interval within that tier's dedicated
 * offering. Returns undefined when the offering or interval is absent.
 */
export function findSubscriptionPackage(
  offerings: RcOfferings,
  tier: SubscriptionTier,
  interval: SubscriptionInterval,
): RcPackage | undefined {
  const offering = offerings.all[TIER_TO_OFFERING_ID[tier]]
  if (!offering) return undefined
  const expectedPackageType = INTERVAL_TO_PACKAGE_TYPE[interval]
  return offering.availablePackages.find((pkg) => pkg.packageType === expectedPackageType)
}

export class HttpIapApi extends MockIapApi {
  private readonly gameId: string
  // One session id per HttpIapApi instance — groups all spends from a single
  // game-launch into one analytics session on the server side.
  private readonly sessionId: string = crypto.randomUUID()

  constructor(gameId: string) {
    super()
    this.gameId = gameId
  }

  async getHardCurrencyBalance(): Promise<number> {
    return callCloudRun<number>('GET', '/v1/wallet')
  }

  async spendCurrency(
    productId: string,
    cost: number,
    options?: SpendCurrencyOptions,
  ): Promise<SpendCurrencyResult> {
    try {
      return await callCloudRun<SpendCurrencyResult>('POST', '/v1/wallet/spend', {
        amount: cost,
        productId,
        sessionId: this.sessionId,
        appId: this.gameId,
        idempotencyKey: crypto.randomUUID(),
        screenName: options?.screenName,
        description: options?.description,
        beneficiaryId: options?.beneficiaryId,
        contentEntryId: options?.contentEntryId,
      })
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : String(error),
      }
    }
  }

  async getSubscriptions(tier?: SubscriptionTier): Promise<RunSubscriptionsResponse> {
    const purchases = await this.getRcInstance()
    const offerings = await purchases.getOfferings()
    return offeringsToSubscriptions(offerings, tier)
  }

  async purchaseSubscription(
    tier: SubscriptionTier,
    interval: SubscriptionInterval,
  ): Promise<PurchaseSubscriptionResponse> {
    assertTierSupportsInterval(tier, interval)
    const purchases = await this.getRcInstance()
    const offerings = await purchases.getOfferings()
    const match = findSubscriptionPackage(offerings, tier, interval)

    if (!match) {
      throw new Error(
        `[RUN:playground] No RC package matches tier=${tier} interval=${interval}. ` +
        `Verify the "${TIER_TO_OFFERING_ID[tier]}" Web Billing offering in the RC dashboard.`,
      )
    }

    try {
      // Opens RC's hosted checkout. Resolves on completion; rejects on
      // cancel/error. RC then fires the webhook to Cloud Run which credits
      // the user — callers should refetch state after this returns.
      await purchases.purchase({ rcPackage: match })
      return { success: true }
    } catch {
      // User cancelled or RC surfaced an error. Mirror the
      // PurchaseSubscriptionResponse contract — success: false, no throw.
      return { success: false }
    }
  }

  async openStore(): Promise<OpenStoreResult> {
    const purchases = await this.getRcInstance()
    const offerings = await purchases.getOfferings()
    // Bucks packs live in the `current` offering (`run_game_iap`); subscriptions
    // do NOT — they have dedicated offerings read by getSubscriptions /
    // purchaseSubscription. Keep openStore on `current` (fallback to `all` only
    // when no current offering is configured, e.g. a bare playground).
    const allPackages: RcPackage[] = offerings.current
      ? offerings.current.availablePackages
      : Object.values(offerings.all).flatMap((o) => o.availablePackages)

    // One-time packs only — exclude anything whose packageType maps to a
    // subscription interval. Subscription purchases go through
    // `purchaseSubscription` / `getSubscriptions`, not the store UI.
    const oneTimePacks = allPackages.filter(
      (pkg) => !(pkg.packageType in PACKAGE_TYPE_TO_INTERVAL),
    )

    let purchased = false
    await showStoreModal(oneTimePacks, async (pkg) => {
      // RC's checkout fires the webhook to Cloud Run on success, which credits
      // the user. Resolving without throwing means RC accepted the purchase.
      await purchases.purchase({ rcPackage: pkg })
      purchased = true
    })

    const newBalance = await this.getHardCurrencyBalance().catch(() => 0)
    return { purchased, newBalance }
  }

  async isUserSubscribed(tier: SubscriptionTier): Promise<boolean> {
    const purchases = await this.getRcInstance()
    const info = await purchases.getCustomerInfo()
    const userTierIndex = highestTierIndexFor(info.activeSubscriptions)
    const requestedIndex = TIER_HIERARCHY.indexOf(tier)
    return requestedIndex >= 0 && userTierIndex >= requestedIndex
  }

  async hasUserMadePurchase(): Promise<boolean> {
    const purchases = await this.getRcInstance()
    const info = await purchases.getCustomerInfo()
    // RC Web SDK keys every past purchase (subs + one-time) in
    // allPurchaseDatesByProduct, so a non-empty record means the user has
    // bought something at some point.
    return Object.keys(info.allPurchaseDatesByProduct).length > 0
  }

  async listDirectPurchaseSkus(gameId: string): Promise<DirectPurchaseSku[]> {
    const response = await callCloudRun<{ success: boolean; skus: DirectPurchaseSku[] }>(
      'GET',
      `/v1/direct-purchase/catalog?gameId=${encodeURIComponent(gameId)}`,
    )
    return response.skus
  }

  /** Purchase a logical direct-purchase SKU through its RC Web package. */
  async purchaseDirectSku(skuId: string, orderId?: string): Promise<{
    provider: 'revenuecat'
    transactionId: string
  }> {
    const cached = orderId ? loadDirectPurchaseReceipt(orderId) : null
    if (cached) return cached

    const skus = await this.listDirectPurchaseSkus(this.gameId)
    const sku = skus.find((candidate) => candidate.sku === skuId)
    if (!sku) {
      throw new Error(`[RUN:playground] Direct-purchase SKU not found: ${skuId}`)
    }
    const webPrice = sku.storePrices.find((price) => price.platform === 'web')
    const productIds = Object.values(webPrice?.stripePriceIds ?? {})
    if (productIds.length === 0) {
      throw new Error(`[RUN:playground] Direct-purchase SKU has no web product: ${skuId}`)
    }

    const purchases = await this.getRcInstance()
    const offerings = await purchases.getOfferings()
    const packages = Object.values(offerings.all).flatMap(
      (offering) => offering.availablePackages,
    )
    const match = packages.find((pkg) => {
      const product = pkg.webBillingProduct ?? pkg.rcBillingProduct
      return product ? productIds.includes(product.identifier) : false
    })
    if (!match) {
      throw new Error(
        `[RUN:playground] No RevenueCat offering package matches direct-purchase SKU ${skuId}.`,
      )
    }

    let result: RcPurchaseResult
    try {
      result = await purchases.purchase({ rcPackage: match })
    } catch (error) {
      if (isRcUserCancelled(error)) {
        // Same contract as the in-app hosts: a dismissed checkout rejects with
        // a RundotApiError whose code is USER_CANCELLED, so games can branch
        // on it instead of seeing a generic failure.
        throw new RundotApiError('USER_CANCELLED', 'Purchase cancelled', 0)
      }
      throw error
    }
    const transactionId = result.storeTransaction?.transactionIdentifier
    if (!transactionId) {
      throw new Error('[RUN:playground] RevenueCat checkout returned no transaction identifier.')
    }
    const receipt = { provider: 'revenuecat' as const, transactionId }
    if (orderId) saveDirectPurchaseReceipt(orderId, receipt)
    return receipt
  }

  completeDirectSku(orderId: string): void {
    if (typeof localStorage === 'undefined') return
    try {
      localStorage.removeItem(directPurchaseReceiptKey(orderId))
    } catch (error) {
      void error
    }
  }

  /**
   * Lazily configure and return the RC Web SDK shared instance. Throws with
   * a clear message when prerequisites (Web Billing key, Firebase sign-in)
   * are missing rather than letting RC surface an opaque error.
   */
  private async getRcInstance(): Promise<RcPurchasesInstance> {
    const playgroundConfig = getPlaygroundConfig()
    const apiKey = playgroundConfig?.revenueCatPublicApiKey
    if (!apiKey) {
      throw new Error(
        '[RUN:playground] iap subscription methods require revenueCatPublicApiKey. ' +
        'Set RUNDOT_REVENUECAT_WEB_BILLING_KEY env var or fill in ' +
        'REVENUECAT_WEB_BILLING_KEYS in firebaseConfigs.ts.',
      )
    }

    const client = await getFirebaseClient()
    const user = client.getCurrentUser()
    if (!user) {
      throw new Error(
        '[RUN:playground] iap subscription methods require an authenticated user.',
      )
    }

    return ensureRcConfigured(apiKey, user.uid)
  }
}
